/* eslint-disable import/order */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import BottomSheetModal from '../BottomSheetModal/BottomSheetModal';
import IconSearch from '../../assets/icons/IconSearch';
import IconX from '../../assets/icons/IconX';
import IconCheckmark from '../../assets/icons/IconCheckmark';
import SearchEmptyState from '../SearchEmptyState/SearchEmptyState';
import { getCountryFlagImage } from '../../../utils/helper-functions/getCountryFlagImage';
import './CountrySelectionModal.sass';
import { LazyImage } from '@pano-project/components/LazyImage/LazyImage';

export type DialCodeItem = {
  dialCode: string;
  name: string;
  countryCode?: string;
};

type CountrySelectionModalPropsBase = {
  isOpen: boolean;
  onClose: () => void;
  langBack: (key: string, useDefault?: boolean) => string;
  hideFlagsInRegionCode?: boolean;
};

type CountryModeProps = CountrySelectionModalPropsBase & {
  mode?: 'country';
  onCountryChange: (countryCode: string) => void;
  countriesList: Record<string, string>;
  selectedValue: string;
  dialCodes?: never;
  onDialCodeChange?: never;
};

type RegionCodeModeProps = CountrySelectionModalPropsBase & {
  mode: 'regionCode';
  onDialCodeChange: (dialCode: string, countryCode?: string | null) => void;
  dialCodes: DialCodeItem[];
  selectedValue: string;
  selectedCountryCode?: string | null;
  countriesList?: never;
  onCountryChange?: never;
};

type CountrySelectionModalProps = CountryModeProps | RegionCodeModeProps;

const CountrySelectionModal: React.FC<CountrySelectionModalProps> = (props) => {
  const { isOpen, onClose, langBack, mode = 'country', hideFlagsInRegionCode = false } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isRegionCode = mode === 'regionCode';

  const countriesArray = useMemo(() => {
    if (isRegionCode && 'dialCodes' in props) {
      return props.dialCodes
        .map(({ dialCode, name, countryCode }) => ({
          code: dialCode,
          name,
          countryCode: countryCode || null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if ('countriesList' in props) {
      return Object.entries(props.countriesList).map(([code, name]) => ({
        code,
        name,
        countryCode: code,
      }));
    }
    return [];
  }, [isRegionCode, props]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return countriesArray;

    const compactQuery = query.replace(/[\s+]/g, '');

    return countriesArray.filter(({ code, name }) => {
      const matchesCode = code.toLowerCase().includes(query);
      const matchesName = name.toLowerCase().includes(query);
      const matchesPlus = `+${code}`.includes(query);
      const matchesCompactCode =
        Boolean(compactQuery) && code.replace(/\s/g, '').includes(compactQuery);
      return matchesCode || matchesName || matchesPlus || matchesCompactCode;
    });
  }, [countriesArray, searchQuery]);

  const selectedValue = props.selectedValue;
  const selectedCountryCode =
    isRegionCode && 'selectedCountryCode' in props ? props.selectedCountryCode : null;

  const handleSelect = (value: string, countryCode?: string | null) => {
    if (isRegionCode && 'onDialCodeChange' in props) {
      props.onDialCodeChange(value, countryCode);
    } else if ('onCountryChange' in props) {
      props.onCountryChange(value);
    }
    onClose();
  };

  const title = isRegionCode
    ? langBack('regionCode') || langBack('Select region code') || 'Select region code'
    : langBack('country') || langBack('Select country') || 'Select country';

  const emptyStateTitle = isRegionCode
    ? langBack('noRegionFound') || 'No region found'
    : langBack('noCountryFound') || 'No country found';

  const emptyStateSubtitle = isRegionCode
    ? langBack('noRegionFoundSubtitle') ||
      "We couldn't find a region or code matching your search, try again"
    : langBack('noCountryFoundSubtitle') ||
      "We couldn't find a country matching your search, try again";

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="country-selection-modal">
      <BottomSheetModal
        isOpen={isOpen}
        onClose={onClose}
        height="calc(100vh - 34px)"
        className="country-selection-modal-wrapper"
        headerContent={
          <div className="country-selection-modal__header">
            <h2 className="country-selection-modal__title">{title}</h2>
          </div>
        }
      >
        <div className="country-selection-modal__content">
          <div className="country-selection-modal__search">
            <div
              className={`country-selection-modal__search-input ${isInputFocused ? '_focused' : ''} ${searchQuery ? '_filled' : ''}`}
            >
              <IconSearch />
              <input
                ref={inputRef}
                type="text"
                placeholder={langBack('Search') || 'Search'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
              />
              {searchQuery && (
                <button
                  className="country-selection-modal__search-clear"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchQuery('');
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 0);
                  }}
                  type="button"
                  aria-label="Clear search"
                >
                  <IconX />
                </button>
              )}
            </div>
          </div>

          <div className="country-selection-modal__list">
            <div className="country-selection-modal__countries">
              {filteredItems.length === 0 ? (
                <div className="country-selection-modal__empty">
                  <SearchEmptyState title={emptyStateTitle} subtitle={emptyStateSubtitle} />
                </div>
              ) : (
                filteredItems.map(({ code, name, countryCode }) => {
                  const isSelected =
                    selectedValue === code &&
                    (!selectedCountryCode || countryCode === selectedCountryCode);
                  return (
                    <button
                      type="button"
                      key={code + countryCode}
                      className={`country-selection-modal__item ${isSelected ? '_selected' : ''} ${isRegionCode && hideFlagsInRegionCode ? '_region-code' : ''}`}
                      onClick={() => handleSelect(code, countryCode)}
                    >
                      {(!isRegionCode || !hideFlagsInRegionCode) && (
                        <div className="country-selection-modal__item-flag">
                          {countryCode && getCountryFlagImage(countryCode) && (
                            <LazyImage src={getCountryFlagImage(countryCode)} alt={countryCode} />
                          )}
                        </div>
                      )}
                      <div className="country-selection-modal__item-content">
                        <div className="country-selection-modal__item-text">
                          <span className="country-selection-modal__item-name">{name}</span>
                          {isRegionCode && (
                            <span className="country-selection-modal__item-dial-code">+{code}</span>
                          )}
                        </div>
                        <div className="country-selection-modal__item-checkmark">
                          <IconCheckmark />
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </BottomSheetModal>
    </div>
  );
};

export default CountrySelectionModal;
